import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { Copy, ArrowUpRight, Send } from "lucide-react";
import { copyCa } from "./Navbar";
import { BUY_URL, TELEGRAM_URL, CA, INTRO_MS } from "../lib/constants";

const LINES = ["THE BIGGEST", "ANIMAL ON EARTH", "JUST ENTERED", "THE MEME GAME."];
const D = INTRO_MS / 1000;

const Bubbles = () =>
  [
    { w: 18, left: "8%", delay: "0s" },
    { w: 30, left: "16%", delay: "1.2s" },
    { w: 12, left: "46%", delay: "0.6s" },
    { w: 24, left: "72%", delay: "1.8s" },
    { w: 15, left: "88%", delay: "0.3s" },
    { w: 22, left: "60%", delay: "2.4s" },
  ].map((b, i) => (
    <span
      key={i}
      className="pointer-events-none absolute bottom-0 rounded-full border-[3px] border-ocean-cyan/25 bg-white/50"
      style={{ width: b.w, height: b.w, left: b.left, animation: `bubble-rise ${7 + i} ${b.delay} linear infinite` }}
    />
  ));

export default function Hero({ onNav }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 14 });
  const sy = useSpring(my, { stiffness: 60, damping: 14 });
  const rotateY = useTransform(sx, [-1, 1], [-9, 9]);
  const rotateX = useTransform(sy, [-1, 1], [7, -7]);
  const whaleX = useTransform(sx, [-1, 1], [-16, 16]);
  const whaleY = useTransform(sy, [-1, 1], [-10, 10]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const driftY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.25]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative overflow-hidden pb-20 pt-32 sm:pt-36 lg:min-h-screen lg:pb-0"
    >
      {/* sky decorations */}
      <div className="pointer-events-none absolute -left-20 top-24 h-72 w-72 rounded-full bg-ocean-yellow/40 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-40 h-80 w-80 rounded-full bg-ocean-sky/30 blur-3xl" />
      <div
        className="animate-spin-slow pointer-events-none absolute -right-16 -top-10 h-56 w-56 rounded-full border-[3px] border-ocean-navy bg-ocean-yellow opacity-90"
        style={{ boxShadow: "inset 0 0 0 14px #FEF9C3" }}
      />
      <Bubbles />

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]"
      >
        {/* Copy side */}
        <div className="min-w-0">
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: D + 0.05, duration: 0.6 }}
            className="sticker-sm inline-flex items-center gap-2 rounded-full bg-white px-4 py-2"
          >
            <span className="h-2.5 w-2.5 animate-ping rounded-full bg-ocean-green" />
            <span className="text-xs font-extrabold uppercase tracking-[0.22em]">
              $BLUE · Live on Solana
            </span>
          </motion.div>

          <h1 className="mt-6 font-display text-[clamp(2.3rem,5.8vw,4.4rem)] font-semibold leading-[1.02] tracking-tight">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`inline-block whitespace-nowrap ${i === 3 ? "marker-underline-coral" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{
                    delay: D + 0.15 + i * 0.13,
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: D + 0.75, duration: 0.6 }}
            className="mt-6 max-w-xl text-base font-medium leading-relaxed text-ocean-muted sm:text-lg"
          >
            Bluewhale ($BLUE) is the biggest animal on earth, now making waves
            on Solana. Big heart. Bigger waves.{" "}
            <span className="font-bold text-ocean-navy">
              No complicated roadmap. No unnecessary promises. Just $BLUE and
              the pod behind it.
            </span>
          </motion.p>

          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: D + 0.9, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-buy-button"
              href={BUY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-push sticker flex items-center gap-2 rounded-2xl bg-ocean-yellow px-7 py-4 font-display text-lg font-semibold"
            >
              Buy $BLUE
              <ArrowUpRight className="h-5 w-5" />
            </a>
            <a
              data-testid="hero-telegram-button"
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-push sticker flex items-center gap-2 rounded-2xl bg-white px-7 py-4 font-display text-lg font-semibold"
            >
              <Send className="h-5 w-5 text-ocean-cyan" />
              Join the Pod
            </a>
          </motion.div>

          <motion.button
            data-testid="hero-ca-copy"
            onClick={copyCa}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: D + 1.05, duration: 0.6 }}
            className="sticker-sm btn-push mt-6 flex w-full items-center gap-3 rounded-2xl bg-ocean-navy px-5 py-3.5 font-mono text-sm font-bold text-white sm:w-fit sm:text-base"
          >
            <span className="truncate">{CA}</span>
            <Copy className="h-4 w-4 shrink-0 text-ocean-sky" />
          </motion.button>
        </div>

        {/* Whale side */}
        <motion.div
          style={{ y: driftY }}
          className="relative mx-auto w-full max-w-[540px]"
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: D + 0.35, duration: 0.8, ease: "backOut" }}
            style={{
              rotateX,
              rotateY,
              transformPerspective: 1000,
              x: whaleX,
              y: whaleY,
            }}
            className="relative"
          >
            <div className="animate-floaty">
              <img
                data-testid="hero-whale-img"
                src="/whale_hero.png"
                alt="Bluewhale cartoon sticker"
                className="w-full drop-shadow-[0_24px_28px_rgba(14,165,233,0.35)]"
                draggable="false"
              />
            </div>

            {/* floating sticker badges */}
            <motion.div
              className="sticker-sm absolute -left-4 top-6 rounded-full bg-ocean-coral px-4 py-2 font-display text-sm font-semibold sm:-left-8"
              animate={{ y: [0, -12, 0], rotate: [-6, -3, -6] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            >
              MEME-FIRST
            </motion.div>
            <motion.div
              className="sticker-sm absolute -right-2 top-1/3 rounded-full bg-ocean-green px-4 py-2 font-display text-sm font-semibold sm:-right-6"
              animate={{ y: [0, 10, 0], rotate: [5, 2, 5] }}
              transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
            >
              COMMUNITY-DRIVEN
            </motion.div>
            <motion.div
              className="sticker-sm absolute bottom-10 left-2 rounded-full bg-ocean-yellow px-4 py-2 font-display text-sm font-semibold"
              animate={{ y: [0, -9, 0], rotate: [-4, -1, -4] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
            >
              MAKING WAVES
            </motion.div>
          </motion.div>

          {/* waves under whale */}
          <svg
            viewBox="0 0 600 120"
            className="relative -mt-6 w-full text-ocean-sky"
            fill="none"
          >
            <path
              d="M0 60c50-30 100-30 150 0s100 30 150 0 100-30 150 0 100 30 150 0v60H0z"
              fill="#38BDF8"
              opacity="0.5"
            />
            <path
              d="M0 80c50-26 100-26 150 0s100 26 150 0 100-26 150 0 100 26 150 0v40H0z"
              fill="#0EA5E9"
              opacity="0.65"
            />
          </svg>
        </motion.div>
      </motion.div>

      {/* wave into next section */}
      <svg
        viewBox="0 0 1440 110"
        preserveAspectRatio="none"
        className="block h-14 w-full text-ocean-bg2 lg:h-20"
        fill="currentColor"
      >
        <path d="M0 55c120-40 240-40 360 0s240 40 360 0 240-40 360 0 240 40 360 0v55H0z" />
      </svg>
    </section>
  );
}
